// @ts-nocheck
import { useQcClassification } from "@/components/quality/qc-form/useQcClassification";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { trpcClient } from "@/lib/trpc";
import { normalizeDefectTallyItems, StageDefectSelectorTable, type DefectTallyItem } from "@/components/quality/StageDefectSelectorTable";
import { QcCategorizedMediaSection } from "@/components/quality/QcCategorizedMediaSection";
import {
  CriteriaTable,
  QcInfoField,
  QcInfoGrid,
  QcInfoValue,
  QcTemplateSelect,
  SamplingRow,
  VerdictBar,
  checklistItemsOf,
  isQuantitativeCriterion,
  normalizeChecklistItems,
  resolveFormStatus,
  useQcSamplingState,
  useQcTemplateCriteria,
  type IsoCriterionResult,
} from "@/components/quality/qc-form";
import { computeQcVerdict, summarizeQcDefects } from "@/lib/aql";
import { Boxes, FileCheck, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { EntityLink } from "@/components/shared/EntityLink";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/stores/auth.store";

import { toVnDateString } from "@apv-erp/status";
export interface QCFinalFormProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSubmit: (data: any) => Promise<void> | void;
  initialData?: any;
  params?: {
    productionOrderId?: string;
    /** Hàng gia công 100% không có lệnh SX: kiểm theo đơn gia công + dòng đơn hàng (04/11/2026) */
    outsourcePoId?: string;
    orderDetailId?: string;
  };
  isSubmitting?: boolean;
  containerMode?: "dialog" | "embedded";
  onCancel?: () => void;
}

const FQC_PRODUCT_CATEGORIES = [
  { code: "HOP_MEM", label: "Hộp giấy mềm (Ivory/Duplex)" },
  { code: "HOP_CUNG", label: "Hộp cứng cao cấp" },
  { code: "THUNG_CARTON", label: "Thùng sóng Carton (3-5 lớp)" },
  { code: "TUI_GIAY", label: "Túi giấy có quai" },
];

const toDateInput = (v: any) => {
  const d = v ? new Date(v) : new Date();
  if (Number.isNaN(d.getTime())) return "";
  return toVnDateString(d);
};

const plain = (t: any) =>
  String(t ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

function initialChecklist(initialData: any): IsoCriterionResult[] {
  return checklistItemsOf(initialData?.checklist);
}

/** Ô số nhỏ trong khối đóng gói */
function PackField({ label, htmlFor, children }: { label: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0 space-y-1">
      <Label htmlFor={htmlFor} className="text-xs font-semibold text-slate-700">
        {label}
      </Label>
      {children}
    </div>
  );
}

/** Mở lại hộp thoại / đổi phiếu thì dựng lại form để lấy đúng dữ liệu ban đầu */
export function QCFinalForm(props: QCFinalFormProps) {
  const key = `${props.open === false ? "closed" : "open"}-${props.initialData?.id || "new"}-${props.params?.productionOrderId || ""}`;
  return <QCFinalFormInner key={key} {...props} />;
}

function QCFinalFormInner({
  open = true,
  onOpenChange,
  onSubmit,
  initialData,
  params,
  isSubmitting = false,
  containerMode = "dialog",
  onCancel,
}: QCFinalFormProps) {
  const isEditMode = !!initialData?.id;
  const currentUser = useAuthStore((st) => st.user);
  const initialPoId = initialData?.productionOrderId || params?.productionOrderId || "";

  // ── Thông tin chung
  const [productionOrderId, setProductionOrderId] = useState<string>(initialPoId);
  const outsourcePoId: string = initialData?.outsourcePoId || params?.outsourcePoId || "";
  const outsourceLineId: string = initialData?.orderDetailId || params?.orderDetailId || "";
  const isOutsourceSource = !initialPoId && Boolean(outsourcePoId);
  const [orderInfo, setOrderInfo] = useState<any>(null);
  const [productTypeId, setProductTypeId] = useState<string>(initialData?.productTypeId || "");
  const [productCategory, setProductCategory] = useState<string>(initialData?.productTypeCode || "HOP_MEM");
  const [templateId, setTemplateId] = useState<string>(initialData?.templateId || "");
  const [inspectorId, setInspectorId] = useState<string>(initialData?.inspectorId || (isEditMode ? "" : currentUser?.id || ""));
  const [referenceSample, setReferenceSample] = useState<string>(initialData?.referenceSample || "");
  const initialDate = toDateInput(initialData?.checkTime);
  const [checkDate, setCheckDate] = useState<string>(initialDate);

  // ── Chỉ tiêu, lỗi, kết luận
  const [checklistItems, setChecklistItems] = useState<IsoCriterionResult[]>(() => initialChecklist(initialData));
  const [defectDetails, setDefectDetails] = useState<DefectTallyItem[]>(() => normalizeDefectTallyItems(initialData?.defectDetails));
  const initialStatus = String(initialData?.status || "").toUpperCase();
  const [override, setOverride] = useState<boolean>(initialStatus === "WARNING");
  const [overrideReason, setOverrideReason] = useState<string>(initialData?.overrideReason || "");
  const [qtyFailed, setQtyFailed] = useState<number>(Number(initialData?.qtyFailed) || 0);
  const [rejectedTouched, setRejectedTouched] = useState<boolean>(isEditMode);
  const [notes, setNotes] = useState<string>(initialData?.notes || "");
  const [attachments, setAttachments] = useState<any[]>(initialData?.attachments || []);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const sampling = useQcSamplingState(
    {
      lotSize: Number(initialData?.lotSize ?? initialData?.qtyChecked ?? 0) || 0,
      mode: initialData?.inspectionMode,
      level: initialData?.aqlLevel,
      aqlMajor: initialData?.aqlStandard,
      aqlMinor: initialData?.aqlMinor ?? undefined,
      sampleSize: initialData?.sampleSize ?? undefined,
      acMajor: initialData?.acValue ?? undefined,
      reMajor: initialData?.reValue ?? undefined,
      acMinor: initialData?.acMinor ?? null,
      reMinor: initialData?.reMinor ?? null,
    },
    { recomputeOnMount: !isEditMode },
  );

  const { data: sysConfigs } = useQuery({
    queryKey: ["configs", "QC_PRODUCT_CATEGORIES"],
    queryFn: () => trpcClient.config.getAll.query(),
    staleTime: 5 * 60 * 1000,
  });
  // Nhóm thành phẩm: danh mục Cấu hình > Chất lượng (dùng chung với mẫu kiểm tra QC)
  const qcClass = useQcClassification();
  const productCategories = qcClass.productGroups.length ? qcClass.productGroups : FQC_PRODUCT_CATEGORIES;

  const { templates, selectTemplate } = useQcTemplateCriteria({
    qcType: "FQC",
    productTypeId: productTypeId || null,
    productTypeCode: productCategory,
    templateId: templateId || null,
    checklistItems,
    onTemplateChange: (id) => setTemplateId(id || ""),
    onChecklistChange: setChecklistItems,
    normalizeOptions: { pendingByDefault: true },
  });

  // Lệnh SX chọn xong: lấy thông tin SP / KH / đơn hàng, cỡ lô theo kế hoạch
  useEffect(() => {
    if (!productionOrderId && !isOutsourceSource) return;
    let alive = true;
    (async () => {
      try {
        const d = await (trpcClient as any).packingSlips.getPackagingDefaults.query(
          isOutsourceSource ? { outsourcePoId, orderDetailId: outsourceLineId || null } : { productionOrderId },
        );
        if (!alive || !d) return;
        setOrderInfo(d);
        if (d.productionOrderId && d.productionOrderId !== productionOrderId) setProductionOrderId(d.productionOrderId);
        if (!isEditMode) {
          if (d.qtyPlan && !(Number(initialData?.lotSize ?? initialData?.qtyChecked) > 0)) sampling.setLotSize(Number(d.qtyPlan));
          // Loại SP theo sản phẩm của lệnh → nạp lại mẫu kiểm tra của loại SP đó (trước đây giữ mẫu theo nhóm mặc định)
          if (d.productTypeId && !initialData?.productTypeId) {
            setProductTypeId(d.productTypeId);
            setTemplateId("");
            setChecklistItems([]);
          }
        }
      } catch {
        // Không lấy được gợi ý: người dùng tự nhập
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionOrderId, isEditMode]);

  const counts = useMemo(() => summarizeQcDefects(defectDetails), [defectDetails]);

  // Kiểm 100%: SL loại mặc định = số sản phẩm lỗi tìm thấy (người kiểm sửa được)
  useEffect(() => {
    if (sampling.mode === "FULL" && !rejectedTouched) setQtyFailed(counts.total);
  }, [sampling.mode, counts.total, rejectedTouched]);

  const lot = sampling.lotSize;
  const failed = Math.min(lot, Math.max(0, Number(qtyFailed) || 0));
  const qtyPassed = Math.max(0, lot - failed);
  const hasQualitative = checklistItems.some((c) => !isQuantitativeCriterion(c));

  const po = initialData?.productionOrder;
  const productName = orderInfo?.productName || po?.productName || po?.product?.productName || initialData?.productName;
  const customerName =
    orderInfo?.customerName || po?.customer?.shortName || po?.customer?.name || initialData?.customer?.name;
  const salesOrderCode = orderInfo?.salesOrderCode || po?.salesOrder?.orderId || initialData?.order?.orderId;
  const poCode = orderInfo?.poCode || po?.orderId || initialData?.outsourcePo?.poId || initialPoId || "PO";

  const handleFormSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    const verdict = computeQcVerdict({ counts, reMajor: sampling.reMajor, reMinor: sampling.reMinor, criteria: checklistItems });
    const status = resolveFormStatus(verdict.verdict, override);
    const next: Record<string, string> = {};
    if (!productionOrderId && !isOutsourceSource) next.productionOrderId = "Chọn lệnh SX";
    if (!inspectorId) next.inspectorId = "Chọn người kiểm";
    if (!(lot > 0)) next.lotSize = "Cỡ lô phải lớn hơn 0";
    if (hasQualitative && !referenceSample.trim()) next.referenceSample = "Ghi mẫu chuẩn dùng để so (số / ngày ký mẫu)";
    if (status === "WARNING" && !overrideReason.trim()) next.overrideReason = "Nhân nhượng phải ghi lý do";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const packingFailed = checklistItems.some(
      (c) => c.result === "FAIL" && /(pack|dong goi|dong thung|tem nhan)/.test(`${plain(c.id)} ${plain(c.name)}`),
    );
    try {
      await onSubmit({
        ...(isEditMode ? { id: initialData.id } : {}),
        ...(isOutsourceSource
          ? { productionOrderId: null, outsourcePoId, orderDetailId: outsourceLineId || null }
          : { productionOrderId }),
        productTypeId: productTypeId || undefined,
        templateId: templateId || undefined,
        inspectorId,
        checkTime:
          checkDate && checkDate !== initialDate
            ? new Date(`${checkDate}T${new Date().toTimeString().slice(0, 8)}`)
            : initialData?.checkTime || undefined,
        status,
        overrideReason: status === "WARNING" ? overrideReason.trim() : null,
        inspectionMode: sampling.mode,
        referenceSample: referenceSample.trim() || null,
        lotSize: lot,
        qtyChecked: lot,
        qtyPassed,
        qtyFailed: failed,
        sampleSize: sampling.sampleSize,
        okSampleQty: Math.max(0, sampling.sampleSize - counts.total),
        ngSampleQty: counts.total,
        aqlLevel: sampling.level,
        aqlStandard: sampling.aqlMajor,
        aqlMinor: sampling.aqlMinor,
        acValue: sampling.acMajor,
        reValue: sampling.reMajor,
        acMinor: sampling.acMinor,
        reMinor: sampling.reMinor,
        criticalDefects: counts.critical,
        majorDefects: counts.major,
        minorDefects: counts.minor,
        checklist: checklistItems,
        defectDetails,
        packagingCheck: !packingFailed,
        notes,
        attachments,
      });
    } catch {
      // Thông báo lỗi do nơi gọi hiển thị
    }
  };

  const formBody = (
    <form id="qc-final-canonical-form" onSubmit={handleFormSubmit} className="min-w-0 space-y-4">
      {/* 1. Thông tin chung + Lấy mẫu */}
      <div className="space-y-3.5 rounded-xl border border-slate-200 bg-white p-4 shadow-2xs">
        <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
          <FileCheck className="h-4 w-4 text-indigo-600" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">Thông tin chung</h3>
        </div>
        <QcInfoGrid>
          {isOutsourceSource ? (
            // Hàng gia công 100%: nguồn là đơn gia công, không chọn lệnh SX
            <QcInfoField label="Đơn gia công">
              <QcInfoValue mono>
                <EntityLink type="outsourceOrder" id={outsourcePoId} code={poCode} />
              </QcInfoValue>
            </QcInfoField>
          ) : (
            <QcInfoField label="Lệnh SX" required error={errors.productionOrderId}>
              <SharedCombobox
                type="productionOrder"
                value={productionOrderId}
                onChange={(val, opt) => {
                  setProductionOrderId(val);
                  if (opt?.original) setOrderInfo((prev) => ({ ...(prev || {}), ...opt.original }));
                }}
                placeholder="Chọn LSX cần nghiệm thu..."
              />
            </QcInfoField>
          )}
          <QcInfoField label="Sản phẩm">
            <QcInfoValue>{productName}</QcInfoValue>
          </QcInfoField>
          <QcInfoField label="Khách hàng">
            <QcInfoValue>{customerName}</QcInfoValue>
          </QcInfoField>
          <QcInfoField label="Đơn hàng">
            <QcInfoValue mono>{salesOrderCode}</QcInfoValue>
          </QcInfoField>
          <QcInfoField label="Loại SP">
            <SharedCombobox
              type="productType"
              value={productTypeId}
              onChange={(val) => {
                setProductTypeId(val);
                setTemplateId("");
                setChecklistItems([]);
              }}
              placeholder="Chọn loại bao bì..."
            />
          </QcInfoField>
          <QcInfoField label="Nhóm SP" htmlFor="fqc-product-category">
            <Select
              value={productCategory}
              onValueChange={(v) => {
                setProductCategory(v);
                setTemplateId("");
                setChecklistItems([]);
              }}
            >
              <SelectTrigger id="fqc-product-category" className="h-9 w-full min-w-0 text-xs font-semibold *:data-[slot=select-value]:truncate">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {productCategories.map((cat: any) => (
                  <SelectItem key={cat.code} value={cat.code}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </QcInfoField>
          <QcInfoField label="Mẫu chuẩn" htmlFor="fqc-reference-sample" required={hasQualitative} error={errors.referenceSample}>
            <Input
              id="fqc-reference-sample"
              value={referenceSample}
              onChange={(e) => setReferenceSample(e.target.value)}
              placeholder="VD: MS-552 ký 20/10"
              className="h-9 text-xs font-semibold"
            />
          </QcInfoField>
          <QcInfoField label="Ngày kiểm" htmlFor="fqc-check-date">
            <Input
              id="fqc-check-date"
              type="date"
              value={checkDate}
              onChange={(e) => setCheckDate(e.target.value)}
              className="h-9 text-xs"
            />
          </QcInfoField>
          <QcInfoField label="Người kiểm" required error={errors.inspectorId}>
            <SharedCombobox type="user" params={{ processGroup: 'QC' }} value={inspectorId} onChange={(val) => setInspectorId(val)} placeholder="Chọn người kiểm..." />
          </QcInfoField>
        </QcInfoGrid>
        <div className="border-t border-slate-100 pt-3">
          <SamplingRow
            sampling={sampling}
            idPrefix="fqc"
            onLotSizeChange={(v) => {
              if ((Number(qtyFailed) || 0) > v) setQtyFailed(v);
            }}
          />
          {errors.lotSize ? <p className="mt-1 text-[11px] font-medium text-destructive">{errors.lotSize}</p> : null}
        </div>
      </div>

      {/* 2. Chỉ tiêu kiểm tra */}
      <CriteriaTable
        items={checklistItems}
        onChange={setChecklistItems}
        toolbar={<QcTemplateSelect templates={templates} value={templateId} onChange={selectTemplate} />}
      />

      {/* 3. Lỗi phát hiện — nguồn duy nhất của số lỗi theo mức */}
      <StageDefectSelectorTable
        processStage="FQC"
        lotSize={lot}
        sampleSize={sampling.sampleSize}
        defectDetails={defectDetails}
        onChange={(items) => setDefectDetails(items)}
        hideSampleSummary
      />

      {/* 4. Kết luận */}
      <VerdictBar
        idPrefix="fqc"
        counts={counts}
        sampleSize={sampling.sampleSize}
        reMajor={sampling.reMajor}
        reMinor={sampling.reMinor}
        criteria={checklistItems}
        override={override}
        onOverrideChange={setOverride}
        overrideReason={overrideReason}
        onOverrideReasonChange={setOverrideReason}
        reasonError={errors.overrideReason}
      >
        <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-3 md:grid-cols-4">
          <PackField label="SL Đạt (bàn giao đóng gói)" htmlFor="fqc-qty-passed">
            <Input
              id="fqc-qty-passed"
              readOnly
              tabIndex={-1}
              value={qtyPassed}
              className="h-9 cursor-default border-emerald-200 bg-emerald-50/40 text-xs font-mono font-bold text-emerald-800"
            />
          </PackField>
          <PackField label="SL loại" htmlFor="fqc-qty-failed">
            <Input
              id="fqc-qty-failed"
              type="number"
              min={0}
              value={qtyFailed}
              onChange={(e) => {
                setRejectedTouched(true);
                setQtyFailed(Math.min(lot, Math.max(0, Math.round(Number(e.target.value) || 0))));
              }}
              className="h-9 border-rose-200 bg-rose-50/30 text-xs font-mono font-bold text-rose-800"
            />
          </PackField>
          <div className="col-span-2 min-w-0 space-y-1">
            <Label htmlFor="fqc-notes" className="text-xs font-semibold text-slate-700">
              Nhận xét
            </Label>
            <Textarea
              id="fqc-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="VD: Rách góc do dao bế cùn, đã thay dao và kiểm lại"
              className="min-h-9 text-xs"
            />
          </div>
        </div>
      </VerdictBar>

      {/* 5. Bàn giao Sản xuất: đóng gói, in tem thùng, nhập kho làm trên Phiếu đóng gói (/factory/packing), không ở phiếu KCS */}
      <div
        className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-2.5 text-xs text-amber-900"
        data-testid="fqc-packing-handover"
      >
        <Boxes className="h-4 w-4 text-amber-700" />
        <span>
          Lô Đạt / Nhân nhượng tự bàn giao cho Sản xuất: tổ đóng gói ghi SL đạt, SL lỗi, người đóng gói, in tem thùng và nhập kho ở
          <b> Xưởng sản xuất › Đóng gói &amp; Nhập kho</b>.
        </span>
        {initialData?.packingSlip?.code ? (
          <span className="font-mono font-bold">
            Phiếu đóng gói {initialData.packingSlip.code} · {initialData.packingSlip.status === "PACKED" ? "đã đóng gói & nhập kho" : "chờ đóng gói"}
          </span>
        ) : null}
        {initialData?.coaNo ? <span className="text-slate-600">Số CoA: <b className="font-mono">{initialData.coaNo}</b></span> : null}
      </div>

      <details className="rounded-xl border border-slate-200 bg-white shadow-2xs">
        <summary className="cursor-pointer px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-700">
          Ảnh &amp; tệp ({Array.isArray(attachments) ? attachments.length : 0})
        </summary>
        <div className="px-2 pb-2">
          <QcCategorizedMediaSection mode="QC" value={attachments} onChange={(v) => setAttachments(v)} />
        </div>
      </details>

      <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-3 sm:flex-row sm:justify-end">
        {containerMode === "dialog" ? (
          <DialogClose asChild>
            <Button type="button" variant="outline" onClick={onCancel} className="h-10">
              Hủy bỏ
            </Button>
          </DialogClose>
        ) : (
          <Button type="button" variant="outline" onClick={onCancel} className="h-10">
            Đóng
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting} className="h-10 bg-indigo-600 px-6 text-sm font-bold hover:bg-indigo-700">
          {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {isSubmitting
            ? "Đang ghi nhận..."
            : isEditMode
              ? "Cập Nhật Biên Bản"
              : "Lưu Biên Bản FQC"}
        </Button>
      </div>
    </form>
  );

  if (containerMode === "embedded") {
    return (
      <>
        <div className="qc-final-form-root w-full">{formBody}</div>
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[92vh] max-w-[95vw] flex-col overflow-hidden bg-slate-50 p-0 lg:max-w-[1080px] xl:max-w-[1280px]">
          <DialogHeader className="sticky top-0 z-10">
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
              {isEditMode ? "Cập nhật biên bản kiểm tra thành phẩm" : "Biên bản kiểm tra thành phẩm"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">Lấy mẫu theo ISO 2859-1 (AQL) hoặc kiểm 100% · lô Đạt bàn giao cho Sản xuất đóng gói</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-3 sm:p-5">{formBody}</div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default QCFinalForm;
